module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('h2', {
    staticClass: "h2a",
    attrs: {
      "id": "ch04"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_26"
    }
  }, [_vm._v("Though the Brain Has Billions of Neurons, Wiring It All Up May Depend upon Very Simple Rules")])]), _c('a', {
    attrs: {
      "id": "page_26"
    }
  }, [_c('p', {
    staticClass: "ch-author"
  }, [_vm._v("Alex L. Kolodkin")])]), _c('p', {
    staticClass: "noindent"
  }, [_c('a', {
    attrs: {
      "id": "page_26"
    }
  }, [_c('span', {
    staticClass: "small"
  }, [_vm._v("THE IMMENSE COMPLEXITY")]), _vm._v(" of neural connections begs the following question: What labels, or cues, could possibly provide a code that instructs their precise organization? Imagine the task of connecting the thousands of phones in the new One World Trade Center building in New York City to their central switching stations—color-coded wires, numbered phone jacks, and lots of unique labels are the only hope to get it right. But to use this “unique label” strategy to wire up the human brain one would need trillions of specific molecular cues. Is such a wiring code even possible? Over one hundred years of neuroscience research has yielded only a few hundred molecules that are known to selectively direct the formation of connections among neurons. But even if all the genes in the human genome produced only wiring cues, that would result in approximately 20,000 unique cues, far fewer than necessary to uniquely code for all the connections in the human brain."), _c('sup')]), _c('a', {
    attrs: {
      "id": "ch04fn1a"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch04fn1"
    }
  }, [_vm._v("1")]), _vm._v(" Recent work in the insect visual system shows that extremely complex neuronal connections among a very large number of neurons can be instructed by very simple rules; each individual neuron can follow these rules on its own and, in the absence of myriad unique labels, wire up intricate and specific connections to many other neurons. So to what extent can a nervous system self-assemble? The answer is, surprisingly, quite a bit.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_27"
    }
  }, [_vm._v("Some of the greatest contributions to our understanding of both the complexity and logic of neural connections were made early in the last century by the Spanish neuroanatomist Santiago Ramón y Cajal."), _c('sup')]), _c('a', {
    attrs: {
      "id": "ch04fn2a"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch04fn2"
    }
  }, [_vm._v("2")]), _vm._v(" Using microscopes primitive by today’s standards and a staining technique that allowed for only a very small fraction of neurons to be labeled in their entirety in a sea of unlabeled neurons, Ramón y Cajal sailed uncharted anatomical waters, characterizing distinct neuronal classes based on their morphology and the architecture of their connections with other neurons. He appreciated the complex and beautiful shapes neurons adopt, and his illustrations are exquisite."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch04fn3a"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch04fn3"
    }
  }, [_vm._v("3")])]), _vm._v(" Ramón y Cajal surmised that the axons extending from a neuron’s cell body, often for very long distances, likely conveyed information to the next neuron down the line, contacting that neuron’s dendrites (arborlike processes emanating from the neuronal cell body), which in turn receive this information and propagate it to the axon and then to the next neuron’s dendrites and so on. This logic allowed Ramón y Cajal to speculate about neural circuit organization throughout the nervous systems of vertebrates and even invertebrates.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In addition to adult brains from creatures of many types, Ramón y Cajal also examined embryonic nervous systems and from this work provided insight into how the complex mature nervous system is assembled. He saw that axons extending to their targets had at their tips a handlike structure we now call a growth cone, the fingers of which, called filopodia, appear to sample the external environment. When neuronal growth cones encounter a cue, either from a distance or very close by, they direct the axon toward an attractive cue and away from a repulsive cue. A wealth of data obtained over the last century has proved Ramón y Cajal extremely prescient."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch04fn4a"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch04fn4"
    }
  }, [_vm._v("4")])]), _vm._v(" We now know the identity of proteins secreted locally that can attract or repel extending neuronal growth cones at long distances and also of proteins associated with cell membranes that act locally to regulate neuronal growth cone guidance. We also know that axons laid down early in development can serve as scaffolds that later-developing axons follow. In this way we have begun to understand how the basic layout of complex neural connections from worms to insects to humans is elaborated. But just as a street map of New York City provides but a glimpse into its multilayered architectural and cultural heritage, we are still in the dark with respect to translating our rudimentary view of nervous system assembly into understanding how trillions of connections in the human brain are successfully wired up. Enter a useful model system: the fruit fly "), _c('em', [_vm._v("Drosophila melanogaster.")])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_28"
    }
  }, [_vm._v("Throughout the history of biology, research organisms apparently less complex than humans have provided invaluable windows into fundamental biological processes; neuroscience is no exception. Pioneering work by several scientists, including the great geneticist Seymour Benzer, showed that "), _c('em', [_vm._v("Drosophila")]), _vm._v(" is an extremely useful model for studying neural development, the transmission of information across synapses from one neuron to the next, overall neural circuit organization, and even complex behaviors."), _c('sup')]), _c('a', {
    attrs: {
      "id": "ch04fn5a"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch04fn5"
    }
  }, [_vm._v("5")]), _vm._v(" With its defined neuroanatomy; unmatched genetic tools; and well-characterized neuronal guidance molecules, which are remarkably similar to human neuronal guidance molecules, the fruit fly is an excellent model system to study how complex neural connections are assembled, even when these connections number far more than the available guidance cues to set them up.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The wiring of the eye to the brain in the fly is one place where we can dig into this problem of neural connections with precision. The insect compound eye consists of about 800 units, called ommatidia, that are easily visible on the surface of the eye ("), _c('a', {
    attrs: {
      "href": "#/xhtml/#fig2"
    }
  }, [_vm._v("figure 2A")]), _vm._v("). Each ommatidium includes a small lens on its outer surface (the curved “cap” you see repeated in "), _c('a', {
    attrs: {
      "href": "#/xhtml/#fig2"
    }
  }, [_vm._v("figure 2A")]), _vm._v("), and beneath each lens in the fly eye resides a group of 8 light-sensitive neurons called photoreceptors (abbreviated PR—we consider only 6 here for simplicity). Photoreceptors in an ommatidium sense light of different wavelengths, ultimately resulting in the transmission of electrical signals along their axons ("), _c('a', {
    attrs: {
      "href": "#/xhtml/#fig2"
    }
  }, [_vm._v("figure 2B")]), _vm._v("). The photoreceptor axons extend to similarly repeated units, called cartridges, in the underlying brain region, which is called the lamina. What is important is that the number and arrangement of photoreceptors within each ommatidium are invariant across all 800 or so ommatidia in each fly eye. An interesting difference among insect eyes is that for diurnal (active during the day) insects, including the butterfly, each of the photoreceptors "), _c('em', [_vm._v("within")]), _vm._v(" an ommatidium points in precisely the same direction in space (parallel arrows in "), _c('a', {
    attrs: {
      "href": "#/xhtml/#fig2"
    }
  }, [_vm._v("figure 2C")]), _vm._v("), and these photoreceptors from a single ommatidium extend their axons together to the same underlying cartridge ("), _c('a', {
    attrs: {
      "href": "#/xhtml/#fig2"
    }
  }, [_vm._v("figure 2C")]), _vm._v("), a relatively simple developmental event. However, insects with nocturnal activity periods, including flies such as "), _c('em', [_vm._v("Drosophila,")]), _vm._v(" have evolved an adaptation, called neural superposition, that increases light capture at twilight or at night without resulting in blurred vision."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch04fn6a"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch04fn6"
    }
  }, [_vm._v("6")])]), _vm._v(" This involves the 6 different photoreceptors, each one in an "), _c('em', [_vm._v("adjacent")]), _vm._v(" ommatidium, pointing precisely in the same direction ("), _c('a', {
    attrs: {
      "href": "#/xhtml/#fig2"
    }
  }, [_vm._v("figure 2B")]), _vm._v(", parallel arrows); the 6 photoreceptors in a "), _c('em', [_vm._v("single")]), _vm._v(" fruit fly ommatidium all point in different directions ("), _c('a', {
    attrs: {
      "href": "#/xhtml/#fig2"
    }
  }, [_vm._v("figure 2D")]), _vm._v(", divergent arrows). Yet the axons of these photoreceptors that point in the same direction and that reside in different adjacent ommatidia somehow manage to extend to the very same underlying cartridge in the lamina ("), _c('a', {
    attrs: {
      "href": "#/xhtml/#fig2"
    }
  }, [_vm._v("figure 2B")]), _vm._v(")."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch04fn7a"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch04fn7"
    }
  }, [_vm._v("7")])]), _vm._v(" Unlike in the butterfly eye, this cannot be accomplished by simply having all photoreceptors in an ommatidium extend their axons directly down to an underlying cartridge (compare "), _c('a', {
    attrs: {
      "href": "#/xhtml/#fig2"
    }
  }, [_vm._v("figures 2B")]), _vm._v(" and "), _c('a', {
    attrs: {
      "href": "#/xhtml/#fig2"
    }
  }, [_vm._v("C")]), _vm._v("), and herein lies the complexity of this wiring problem. Though "), _c('a', {
    attrs: {
      "href": "#/xhtml/#fig2"
    }
  }, [_vm._v("figure 2B")]), _vm._v(" shows the wiring of only one set of 6 photoreceptors in adjacent ommatidia connecting to a single lamina cartridge in the fly brain, one must realize that this complex axon sorting is happening simultaneously for "), _c('em', [_vm._v("all")]), _vm._v(" the approximately 5,000 photoreceptor axons in "), _c('em', [_vm._v("all")]), _vm._v(" 800 ommatidia of the fly eye, a form of choreography easily outshining a Super Bowl halftime show. Producing individual labels for each photoreceptor-lamina connection is likely not the solution to preventing photoreceptor axons from forming a tangled mess as they extend across one another to their specific target cartridges.")]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('a', {
    attrs: {
      "id": "page_29"
    }
  }), _c('a', {
    attrs: {
      "id": "fig2"
    }
  }, [_c('img', {
    attrs: {
      "alt": "Images",
      "src": "images/f041-3.jpg"
    }
  })])]), _c('a', {
    attrs: {
      "id": "fig2"
    }
  }, [_c('p', {
    staticClass: "caption"
  }, [_c('span', {
    staticClass: "small"
  }, [_vm._v("FIGURE 2.")]), _vm._v(" Neural connectivity in the insect eye. (A) The surface of the fruit fly eye, showing the regular arrangement of about 800 ommatidial units. Note the curved surface over each single ommatidium; this is the lens, which focuses light onto the underlying photoreceptors. (B) A schematic showing connections between the eye and the brain in fruit fly "), _c('em', [_vm._v("Drosophila.")]), _vm._v(" Connections from the six different photoreceptors (PRs) in the eye that all point in exactly the same direction (arrows at top) are located in "), _c('em', [_vm._v("adjacent")]), _vm._v(" ommatidia and must extend axons through complex pathways to the same underlying target (cartridge) in the brain. Axons from two of these PRs are labeled PR#3 and PR#6. (C) A schematic of eye-to-brain connections in the butterfly. Unlike in the fly, PRs that point in exactly the same direction (arrows at top) are all located in a "), _c('em', [_vm._v("single")]), _vm._v(" ommatidium. Their axons extend directly to the same cartridge in the underlying brain, a much simpler wiring event than in the fly. (D) A schematic of eye-to-brain connections in the fly, here showing that PRs in the "), _c('em', [_vm._v("same")]), _vm._v(" ommatidium all point in different directions (arrows at top), and their axons extend to six different cartridges in the underlying brain.")])]), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "fig2"
    }
  }), _c('a', {
    attrs: {
      "id": "page_30"
    }
  }, [_vm._v("P. Robin Hiesinger and colleagues investigated the mechanisms underlying this wiring problem in the developing fly eye in a manner Ramón y Cajal would have appreciated: they simply looked."), _c('sup')]), _c('a', {
    attrs: {
      "id": "ch04fn8a"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch04fn8"
    }
  }, [_vm._v("8")]), _vm._v(" Taking advantage of modern microscopy techniques that allow imaging of individual photoreceptor axons and their growth cones as they extend to their targets, the team defined simple rules that underlie wiring in the fly eye. The microscope is a special instrument that allows the observer to peer deep inside living tissues and observe with great clarity the detailed morphology of single neurons, their axons, and their growth cones."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch04fn9a"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch04fn9"
    }
  }, [_vm._v("9")])]), _vm._v(" The experiment was simply to watch these photoreceptor axons and their growth cones over the approximately thirty hours it takes for them to extend from the eye into the brain during fly development. Careful analysis of these time-lapse movies allowed each of the six photoreceptors to be unambiguously identified in any one of the fly eye’s 800 ommatidia.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The central observation is that each of the six photoreceptors in any individual ommatidium exhibits a different pattern of axon outgrowth once it contacts the lamina. For example, the axon from photoreceptor #3 always touches down in the lamina and then extends southeast at a fixed rate, regardless of which ommatidium in the eye one observes ("), _c('a', {
    attrs: {
      "href": "#/xhtml/#fig2"
    }
  }, [_vm._v("figures 2B")]), _vm._v(" and "), _c('a', {
    attrs: {
      "href": "#/xhtml/#fig2"
    }
  }, [_vm._v("D")]), _vm._v("). The axon from photoreceptor #6 touches down in the lamina and extends west, again at a fixed rate but one different from photoreceptor #3’s rate of axon extension. And so on for the other four photoreceptors. However, if one compares the axon extension patterns of photoreceptors #1–6 in "), _c('em', [_vm._v("different")]), _vm._v(" ommatidia across the eye, they are identical. This suggests that each of the six individual photoreceptors has a unique intrinsic growth program that is executed in the same manner in every ommatiduim, defining a rule that underlies the assembly of complex neural wiring in the fly eye. If this rule is followed and all 6 photoreceptors extend in their designated directions and at their distinct speeds, the result is quite remarkable: the 6 photoreceptor axon growth cones extending from the 6 adjacent ommatidia that see the same point in space all meet at the same time at a single lamina cartridge, and then they stop ("), _c('a', {
    attrs: {
      "href": "#/xhtml/#fig2"
    }
  }, [_vm._v("figure 2B")]), _vm._v("). This defines a second rule, which is simply that axon extension ceases "), _c('em', [_vm._v("only")]), _vm._v(" when all 6 photoreceptor axon growth cones together contact each other and not before. So photoreceptor axons that point in the exact same direction can navigate through a teeming meshwork of axons and growth cones and still keep going since glancing contacts with one or a few growth cones that extend from photoreceptors pointing in different directions will not stop their extension. This mode of photoreceptor wiring to the brain in the fly is extremely accurate; mistakes rarely occur, and the result is that each lamina cartridge in the brain is innervated only by photoreceptors that point in the exact same direction. Therefore, processing of visual stimuli at higher brain centers is greatly simplified since directional information is already sorted out at the level of the lamina cartridge, the first relay station following photoreceptor sensation of light in the insect visual system. Computational modeling by Hiesinger and co-workers shows that the simultaneous meeting of the 6 photoreceptor axon growth cones is enough to ensure correct targeting; no cue in the lamina cartridge is required. Therefore, the seemingly intractable problem of how to wire up the 5,000 photoreceptor axons, all at the same time, in the complex pattern required for neural superposition is actually accomplished by just 6 distinct photoreceptor axon growth programs. Neural superposition patterning emerges as these photoreceptor axon growth programs are executed during fly eye development. Apparently, no set of complex guidance cues is required to uniquely guide each of the approximately 5,000 photoreceptor axons to its target.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_31"
    }
  }, [_vm._v("What are the implications of this work for understanding mammalian brain connectivity? While there is no arrangement of neurons in the human brain directly analogous to the almost crystalline organization of neurons in the fly eye, it is clear that a limited number of distinct neuronal cell types populate different mammalian brain regions. Neurons of the same type in the mammalian brain adopt remarkably similar patterns of axon and dendrite branching as they establish their unique connections with one another. Of course, several outside influences can act on neurons to sculpt these connections during embryonic and early postnatal neural development. These factors include guidance cues and even electrical signaling to a neuron by other neurons in a circuit. However, this work in flies reminds us that there are alternatives to instructing each individual connection in a complex neural network. It even leads to optimism regarding clinical approaches toward ameliorating damage to neurons from stroke or injury."), _c('sup')]), _c('a', {
    attrs: {
      "id": "ch04fn10a"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch04fn10"
    }
  }, [_vm._v("10")]), _vm._v(" If the history of neuroscience research is any indication, we can expect this work in flies to lead to a greater understanding of how simple rules establish complex connections among neurons in the human brain."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch04fn11a"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch04fn11"
    }
  }, [_vm._v("11")])])]), _vm._v(" "), _c('h3', {
    staticClass: "h3"
  }, [_c('a', {
    attrs: {
      "id": "page_32"
    }
  }, [_c('span', {
    staticClass: "small"
  }, [_vm._v("NOTES")])])]), _c('a', {
    attrs: {
      "id": "page_32"
    }
  }), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "page_32"
    }
  }), _c('a', {
    attrs: {
      "id": "ch04fn1"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch04fn1a"
    }
  }, [_vm._v("1")]), _vm._v(". A combinatorial code for neural wiring is also a formal possibility. However, even if several hundred cues were able to generate a large number of unique combinations, this solution kicks the can down the road since it is an equally daunting task to precisely distribute these cues so as to selectively determine extremely complex patterns of connections among a very large number of neurons.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch04fn2"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch04fn2a"
    }
  }, [_vm._v("2")]), _vm._v(". S. Ramón y Cajal, "), _c('em', [_vm._v("Histology of the Nervous System,")]), _vm._v(" trans. N. Swanson and L. W. Swanson (Oxford: Oxford University Press, 1995; originally published in Spanish in 1909).")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch04fn3"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch04fn3a"
    }
  }, [_vm._v("3")]), _vm._v(". J. DeFelipe, "), _c('em', [_vm._v("Cajal’s Butterflies of the Soul")]), _vm._v(" (Oxford: Oxford University Press, 2010).")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch04fn4"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch04fn4a"
    }
  }, [_vm._v("4")]), _vm._v(". A. L. Kolodkin and M. Tessier-Lavigne, “Mechanisms and Molecules of Neuronal Wiring: A Primer,” "), _c('em', [_vm._v("Cold Spring Harbor Perspectives in Biology")]), _vm._v(" 3 (2011): 1–14.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch04fn5"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch04fn5a"
    }
  }, [_vm._v("5")]), _vm._v(". S. Benzer, “From Gene to Behavior,” "), _c('em', [_vm._v("Journal of the American Medical Association")]), _vm._v(" 218 (1971): 1015–1022; D. Anderson and S. Brenner, “Obituary: Seymour Benzer (1921–2007),” "), _c('em', [_vm._v("Nature")]), _vm._v(" 451 (2008): 139.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch04fn6"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch04fn6a"
    }
  }, [_vm._v("6")]), _vm._v(". E. Agi, M. Langen, S. J. Altschuler, L. F. Wu, T. Zimmermann, and P. R. Hiesinger, “The Evolution and Development of Neural Superposition,” "), _c('em', [_vm._v("Journal")]), _vm._v(" "), _c('em', [_vm._v("of Neurogenetics")]), _vm._v(" 28 (2014): 216–232. This arrangement of neural wiring in the eyes of “advanced” flies such as "), _c('em', [_vm._v("Drosophila")]), _vm._v(" is called “neural superposition,” and it has remained a mystery until now how this complex pattern of connections between photoreceptors in adjacent ommatidia and a single underlying lamina cartridge is established.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "page_33"
    }
  }), _c('a', {
    attrs: {
      "id": "ch04fn7"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch04fn7a"
    }
  }, [_vm._v("7")]), _vm._v(". Ibid.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch04fn8"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch04fn8a"
    }
  }, [_vm._v("8")]), _vm._v(". M. L. Langen, E. Agi, D. J. Altschuler, L. F. Wu, S. J. Altschuler, and P. R. Hiesinger, “The Developmental Rules of Neural Superposition in Drosophila,” "), _c('em', [_vm._v("Cell")]), _vm._v(" 162 (2015): 120–133.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch04fn9"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch04fn9a"
    }
  }, [_vm._v("9")]), _vm._v(". The photoreceptor neurons were labeled using genetic tricks so that only a very few photoreceptors in any one fly eye expressed the Green Fluorescent Protein (GFP), originally isolated from the jellyfish; GFP allows individual photoreceptor neurons to be easily observed in real time as they navigate to their targets.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch04fn10"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch04fn10a"
    }
  }, [_vm._v("10")]), _vm._v(". The direct introduction into the injured human brain of specific neural cell types, derived from stem cells that have been coaxed to differentiate into these cell types, is one approach under study for replacing neurons damaged by stroke or injury. It seems likely that distinct neuronal cell types have intrinsic growth programs, and an exciting possibility is that these programs might help guide the appropriate wiring of these new neurons into existing circuits and thereby facilitate repair of the nervous system.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch04fn11"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch04fn11a"
    }
  }, [_vm._v("11")]), _vm._v(". Credit to Thomas Lloyd for panel A and to Natalie Hamilton for panels B–D in "), _c('a', {
    attrs: {
      "href": "#/xhtml/#fig2"
    }
  }, [_vm._v("figure 2")]), _vm._v(".")])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-c16ef8f2","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/xhtml/ch04.vue
// module id = 87
// module chunks = 0